import { T, tt } from '@agos/shared'
import { Check, Cookie, Settings2, X } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'wouter'

import { UNDECIDED, buildConsent, readConsent, writeConsent } from './consent'

/**
 * The cookie banner.
 *
 * **The optional categories start off** — FB-C11. Legacy opens the customise panel with both switched
 * on, so a visitor who opens it to make a choice and makes none is recorded as consenting to Google
 * Analytics and the Meta Pixel, both of which are configured and live on this data. That is the one
 * thing here that deliberately differs from legacy, and it is a consent bug rather than a design one.
 *
 * **Everything else is legacy's, including the words** — FB-C49. An earlier draft was a full-width bar
 * across the bottom with sentences of its own invention (*Prijať všetko* where legacy says *Súhlasím so
 * všetkými*, one run-on line where legacy has a heading and a paragraph, no *Viac informácií* link at
 * all). It appeared in the corner of all 36 screenshot pairs, which is how it was eventually noticed.
 *
 * The card is bottom-right on desktop and inset above the mobile tab bar, non-blocking until the
 * customise panel is opened — legacy dims the page only then, so a visitor who wants to read the menu
 * before deciding can.
 *
 * The gate itself lives in `trackers.ts`: nothing before a decision, GA behind `analytics`, the pixels
 * behind `marketing`, re-checked on the decision event rather than only at boot.
 */

const PRIMARY_BUTTON =
  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 transition-colors'
const QUIET_BUTTON =
  'flex-1 px-3 py-2.5 rounded-xl border border-border bg-background text-foreground font-semibold text-sm hover:bg-muted transition-colors'
const CATEGORY_ROW =
  'flex items-start justify-between gap-3 p-3 rounded-xl bg-muted/30 border border-border'

export function CookieConsentBanner() {
  // Read on the first render rather than in an effect. Legacy starts *decided* and corrects itself in
  // an effect to avoid a flash; a lazy initialiser has the answer before the first paint and avoids
  // both the flash and the cascading `setState` the shell's lint rule refuses — the third time that
  // rule has produced the better design rather than merely a compliant one.
  const [decided, setDecided] = useState(() => readConsent() !== null)
  const [customising, setCustomising] = useState(false)
  const [choice, setChoice] = useState(UNDECIDED)

  if (decided) return null

  function decide(next: { analytics: boolean; marketing: boolean }) {
    writeConsent(buildConsent(next, new Date()))
    setDecided(true)
  }

  return (
    <>
      {/* Legacy dims the page only once the panel is open, so the banner stays out of the way of
          somebody who wants to read the menu before answering it. */}
      {customising && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          onClick={() => setCustomising(false)}
        />
      )}

      <div
        className="fixed bottom-20 md:bottom-3 inset-x-3 z-[70] rounded-3xl bg-card border border-border shadow-2xl md:left-auto md:right-3 md:max-w-md"
        data-cookie-banner
      >
        {!customising ? (
          <div className="p-4 sm:p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-foreground text-sm">
                  <T>Používame cookies</T>
                </h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  <T>
                    Nutné cookies sú vždy aktívne. Voliteľne nám môžete povoliť aj analytické a
                    marketingové cookies, aby sme mohli zlepšovať web a posielať vám relevantné ponuky.
                  </T>{' '}
                  <Link
                    href="/page/ochrana-osobnych-udajov"
                    className="text-primary underline hover:no-underline"
                  >
                    <T>Viac informácií</T>
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <button
                type="button"
                data-cookie-accept-all
                onClick={() => decide({ analytics: true, marketing: true })}
                className={PRIMARY_BUTTON}
              >
                <Check className="w-4 h-4" />
                <T>Súhlasím so všetkými</T>
              </button>
              <button
                type="button"
                data-cookie-reject-all
                onClick={() => decide({ analytics: false, marketing: false })}
                className={QUIET_BUTTON}
              >
                <T>Odmietnuť</T>
              </button>
              <button
                type="button"
                data-cookie-customise
                onClick={() => setCustomising(true)}
                aria-label={tt('Prispôsobiť cookies')}
                className="px-3 py-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                <Settings2 className="w-4 h-4" />
                <span className="sm:hidden">
                  <T>Nastaviť</T>
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-5 space-y-3" data-cookie-options>
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-foreground text-base flex items-center gap-2">
                <Settings2 className="w-4 h-4" /> <T>Nastavenie cookies</T>
              </h3>
              <button
                type="button"
                data-cookie-close
                onClick={() => setCustomising(false)}
                aria-label={tt('Zavrieť')}
                className="p-1 -m-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2">
              <div className={CATEGORY_ROW}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    <T>Nutné</T>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <T>Login, košík, bezpečnosť. Bez nich web nefunguje.</T>
                  </p>
                </div>
                {/* A badge rather than a ticked, disabled checkbox: the session and branch cookies are
                    not a choice, and drawing them as one would be the mirror image of FB-C11. */}
                <span
                  data-cookie-necessary
                  className="text-[10px] font-bold uppercase text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0"
                >
                  <T>Vždy</T>
                </span>
              </div>

              <label className={`${CATEGORY_ROW} cursor-pointer`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    <T>Analytické</T>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <T>Anonymná štatistika návštevnosti - pomáha zlepšovať web.</T>
                  </p>
                </div>
                <input
                  type="checkbox"
                  data-cookie-analytics
                  checked={choice.analytics}
                  onChange={(event) => setChoice({ ...choice, analytics: event.target.checked })}
                  className="w-5 h-5 rounded accent-primary cursor-pointer flex-shrink-0 mt-0.5"
                />
              </label>

              <label className={`${CATEGORY_ROW} cursor-pointer`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    <T>Marketingové</T>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <T>Personalizované ponuky, retargeting reklám.</T>
                  </p>
                </div>
                <input
                  type="checkbox"
                  data-cookie-marketing
                  checked={choice.marketing}
                  onChange={(event) => setChoice({ ...choice, marketing: event.target.checked })}
                  className="w-5 h-5 rounded accent-primary cursor-pointer flex-shrink-0 mt-0.5"
                />
              </label>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                data-cookie-save
                onClick={() => decide(choice)}
                className={PRIMARY_BUTTON}
              >
                <Check className="w-4 h-4" />
                <T>Uložiť výber</T>
              </button>
              <button
                type="button"
                data-cookie-accept-all
                onClick={() => decide({ analytics: true, marketing: true })}
                className="px-3 py-2.5 rounded-xl border border-border bg-background text-foreground font-semibold text-sm hover:bg-muted transition-colors"
              >
                <T>Povoliť všetko</T>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
